import {
  CLEANUP_OBJECTS,
  cleanupCrmName,
  cleanupCrmType,
  cleanupObjectNoun,
  cleanupObjectTitle,
  isCleanupTrigger,
  type CleanupCrm,
} from '@/containers/playbooks/custom-builder/cleanup-rules';
import type {
  CleanupObject,
  CustomPlaybookDto,
  TriggerConfig,
} from '@/types/custom-playbooks';
import type { AgentDto } from '@/types/gtm-agents';
import type {
  DuplicateDecideScope,
  DuplicateGroupDetail,
  DuplicateGroupFilters,
  DuplicateGroupRow,
  DuplicateGroupsSummaryDto,
  DuplicateKeptReason,
  DuplicateMatchReason,
  DuplicateMember,
  DuplicateReviewStatus,
} from '@/types/duplicate-groups';
import type { LibraryTab } from '../library-toolbar.component';

/**
 * The review screen's words and rules (W5 design 10.3), pure so they are
 * tested here: which groups each filter shows, what "Merge all shown" covers,
 * how a group's reasons, flags and kept record read, who may act on it, and
 * the column diff a row opens to.
 */

/* ── The agent's cleanup play ─────────────────────────────────────────── */

export interface CleanupPlay {
  /** The play's trigger is a CRM duplicates trigger. */
  cleanup: boolean;
  /**
   * A Merge duplicates step: without one the play only reports. Unknown
   * while the play itself can't be read; the groups' own mode says then.
   */
  hasMerge: boolean | undefined;
  crmType: CleanupCrm | undefined;
  /** The objects its trigger checks. */
  objects: CleanupObject[];
}

/**
 * Whether the agent's play is a cleanup play, and what it can do: from the
 * play when it has loaded, else from the play's trigger as the agent reports
 * it (the review screen reads no playbooks route, so it needs no other flag).
 */
export function cleanupPlayOf(
  agent: Pick<AgentDto, 'triggers'> | undefined,
  play?: Pick<CustomPlaybookDto, 'triggerType' | 'graph'>
): CleanupPlay {
  const nodes = play?.graph?.nodes ?? [];
  const playTrigger = agent?.triggers.find((item) => item.source === 'play');
  const config = (nodes.find((node) => node.type === 'trigger')?.config ??
    playTrigger?.config) as Partial<TriggerConfig> | undefined;
  const cleanup =
    play?.triggerType === 'crm_cleanup' ||
    isCleanupTrigger(config) ||
    playTrigger?.kind === 'crm_duplicates';
  const objects = Array.isArray(config?.objects) ? config.objects : [];
  return {
    cleanup,
    hasMerge: play
      ? nodes.some((node) => node.type === 'merge_duplicates')
      : undefined,
    crmType: cleanup ? cleanupCrmType(config) : undefined,
    objects: objects.filter(isObject),
  };
}

/* ── Filters ───────────────────────────────────────────────────────────── */

/**
 * The review screen's filters. The first ones show groups waiting for review
 * and the ones merging now; the last four the groups done with.
 */
export type ReviewFilter =
  | 'all'
  | 'exact'
  | 'possible'
  | 'possible_clean'
  | CleanupObject
  | 'merged'
  | 'skipped'
  | 'failed'
  | 'not_duplicates';

const DONE_FILTERS: Readonly<
  Record<'merged' | 'skipped' | 'failed' | 'not_duplicates', string>
> = {
  merged: 'Merged',
  skipped: 'Skipped',
  failed: 'Failed',
  not_duplicates: 'Not duplicates',
};

const ALL_OBJECTS: readonly CleanupObject[] = [
  'account',
  'company',
  'contact',
  'lead',
];

function isObject(filter: string): filter is CleanupObject {
  return (ALL_OBJECTS as readonly string[]).includes(filter);
}

/** A filter that shows groups waiting for review (and merging). */
export function isOpenFilter(filter: ReviewFilter): boolean {
  return !(filter in DONE_FILTERS);
}

/**
 * The objects the filters offer, in the CRM's order: the ones the play's
 * trigger checks, and any other the agent has open groups for.
 */
export function reviewObjects(
  crmType: CleanupCrm | undefined,
  playObjects: readonly CleanupObject[],
  summary: Pick<DuplicateGroupsSummaryDto, 'byObject'> | undefined
): CleanupObject[] {
  const order = [
    ...CLEANUP_OBJECTS[crmType ?? 'salesforce'],
    ...ALL_OBJECTS.filter(
      (object) => !CLEANUP_OBJECTS[crmType ?? 'salesforce'].includes(object)
    ),
  ];
  return order.filter(
    (object) =>
      playObjects.includes(object) || (summary?.byObject[object] ?? 0) > 0
  );
}

/** The filters as tabs: All, Exact, Possible, Possible no flags, one per object, then the done ones. */
export function reviewTabs(
  objects: readonly CleanupObject[]
): LibraryTab<ReviewFilter>[] {
  return [
    { value: 'all', label: 'All' },
    { value: 'exact', label: 'Exact' },
    { value: 'possible', label: 'Possible' },
    { value: 'possible_clean', label: 'Possible no flags' },
    ...objects.map((object) => ({
      value: object,
      label: cleanupObjectTitle(object),
    })),
    ...(Object.keys(DONE_FILTERS) as (keyof typeof DONE_FILTERS)[]).map(
      (value) => ({ value, label: DONE_FILTERS[value] })
    ),
  ];
}

/** The groups a review filter shows: waiting for review and merging, or one done status. */
const ACTIVE: readonly DuplicateReviewStatus[] = ['open', 'merging'];

export function filtersFor(filter: ReviewFilter): DuplicateGroupFilters {
  switch (filter) {
    case 'all':
      return { status: ACTIVE };
    case 'exact':
      return { status: ACTIVE, confidence: 'exact' };
    case 'possible':
      return { status: ACTIVE, confidence: 'possible' };
    case 'possible_clean':
      return { status: ACTIVE, confidence: 'possible', flags: 'none' };
    case 'merged':
    case 'skipped':
    case 'failed':
    case 'not_duplicates':
      return { status: filter };
    default:
      return { status: ACTIVE, object: filter };
  }
}

/**
 * What "Merge all shown" covers, resolved on the server: the open groups
 * under the filter. None for a done filter.
 */
export function scopeFor(filter: ReviewFilter): DuplicateDecideScope | null {
  if (!isOpenFilter(filter)) return null;
  const { confidence, flags, object } = filtersFor(filter);
  return {
    status: 'open',
    ...(object ? { object } : {}),
    ...(confidence ? { confidence } : {}),
    ...(flags ? { flags } : {}),
  };
}

/**
 * The scope "Merge all shown" merges, offered only on the filters that hold
 * no flagged group (an exact group never carries a flag). On any other
 * filter it would merge flagged groups, and ones the reviewer never loaded,
 * unseen; null there.
 */
export function mergeAllScopeFor(
  filter: ReviewFilter
): DuplicateDecideScope | null {
  return filter === 'exact' || filter === 'possible_clean'
    ? scopeFor(filter)
    : null;
}

/**
 * How many open groups a filter covers, from the summary: what "Merge all
 * shown" says and sends as `expectedCount`. Exact groups never carry a flag,
 * so possible groups without one are the open ones less the flagged and the
 * exact.
 */
export function openCountFor(
  filter: ReviewFilter,
  summary: DuplicateGroupsSummaryDto | undefined
): number {
  if (!summary || !isOpenFilter(filter)) return 0;
  const open = summary.byStatus.open ?? 0;
  switch (filter) {
    case 'all':
      return open;
    case 'exact':
      return summary.byConfidence.exact ?? 0;
    case 'possible':
      return summary.byConfidence.possible ?? 0;
    case 'possible_clean':
      return Math.max(
        0,
        open - (summary.flagged ?? 0) - (summary.byConfidence.exact ?? 0)
      );
    default:
      return isObject(filter) ? (summary.byObject[filter] ?? 0) : 0;
  }
}

/** The filter a `review:<filter>` view opens on; `review:open` is All. */
export function reviewFilterOf(view: string | undefined): ReviewFilter {
  const name = view?.startsWith('review:') ? view.slice('review:'.length) : '';
  if (name === 'open' || !name) return 'all';
  const known: readonly string[] = [
    'all',
    'exact',
    'possible',
    'possible_clean',
    ...ALL_OBJECTS,
    ...Object.keys(DONE_FILTERS),
  ];
  return known.includes(name) ? (name as ReviewFilter) : 'all';
}

/* ── Counts ────────────────────────────────────────────────────────────── */

export const REVIEW_COUNTS: readonly {
  status: DuplicateReviewStatus;
  label: string;
}[] = [
  { status: 'open', label: 'Open' },
  { status: 'merging', label: 'Merging' },
  { status: 'merged', label: 'Merged' },
  { status: 'skipped', label: 'Skipped' },
  { status: 'failed', label: 'Failed' },
  { status: 'not_duplicates', label: 'Not duplicates' },
];

/* ── A group in words ──────────────────────────────────────────────────── */

const MATCH_WORDS: Readonly<Record<string, string>> = {
  email: 'Same email',
  linkedin: 'Same LinkedIn',
  cleanlist_id: 'Same Cleanlist ID',
  domain: 'Same domain',
  name: 'Similar name',
};

/** The reason chips, once per kind, in the order the scan ranked them. */
export function matchChips(reasons: readonly DuplicateMatchReason[]): string[] {
  const chips: string[] = [];
  for (const reason of reasons) {
    const words =
      reason.kind === 'domain' && reason.subdomain
        ? 'Related domain'
        : (MATCH_WORDS[reason.kind] ??
          'Same ' + reason.kind.replace(/_/g, ' '));
    if (!chips.includes(words)) chips.push(words);
  }
  return chips;
}

const FLAG_WORDS: Readonly<Record<string, string>> = {
  names_differ: 'Names differ',
  different_accounts: 'Different accounts',
  account_merge_pending: 'Accounts merge first',
  conflicting_linkedin: 'Different LinkedIn',
  conflicting_email_domain: 'Different email domains',
  subdomain_match: 'Subdomain',
  account_hierarchy: 'Parent and child',
  different_country: 'Different countries',
  different_record_types: 'Different record types',
  multiple_open_deals: 'Open deals on several',
  different_rep_owners: 'Different owners',
  consent_differs: 'Consent differs',
  loser_opted_out: 'Opted out',
  large_group: 'Large group',
};

/** A flag's chip; an unknown flag reads as itself in sentence case. */
export function flagLabel(flag: string): string {
  const known = FLAG_WORDS[flag];
  if (known) return known;
  const words = flag.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The day in a keep rule's detail ("created 2019-03-02"), as "Mar 2, 2019".
 * The day, not the year: records created minutes apart read "(2026)" as if
 * years stood between them.
 */
function dayOf(detail: string): string | null {
  const match = /(\d{4})-(\d{2})-(\d{2})/.exec(detail);
  if (!match) return null;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  );
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function firstNumber(detail: string): string | null {
  return /(\d+)/.exec(detail)?.[1] ?? null;
}

/** "Kept: oldest record (Mar 2, 2019)": why the kept record is kept. */
export function keptWords(
  reason: DuplicateKeptReason | null | undefined
): string {
  const detail = reason?.detail ?? '';
  const day = dayOf(detail);
  const count = firstNumber(detail);
  switch (reason?.rule) {
    case 'oldest':
      return 'Kept: oldest record' + (day ? ' (' + day + ')' : '');
    case 'newest':
      return 'Kept: newest record' + (day ? ' (' + day + ')' : '');
    case 'open_deals':
      return 'Kept: most open deals' + (count ? ' (' + count + ')' : '');
    case 'owner_is_rep':
      return 'Kept: owned by a rep';
    case 'recent_activity':
      return 'Kept: latest activity' + (day ? ' (' + day + ')' : '');
    case 'most_complete':
      return 'Kept: most complete' + (count ? ' (' + count + ' fields)' : '');
    case 'opted_out':
      return 'Kept: opted out of email';
    case 'lowest_id':
      return 'Kept: lowest record ID';
    case 'picked':
      return 'Kept: picked in review';
    default:
      return 'Kept: suggested record';
  }
}

/** "Jane Doe", or the object's name when the scan had none. */
export function groupName(
  row: Pick<DuplicateGroupRow, 'displayName' | 'object'>
): string {
  return row.displayName?.trim() || cleanupObjectTitle(row.object, false);
}

/** "3 records". */
export function recordCount(count: number): string {
  return count.toLocaleString() + (count === 1 ? ' record' : ' records');
}

const STATUS_REASON_WORDS: Readonly<Record<string, string>> = {
  rules_changed: 'Rules changed',
  needs_refresh: 'Needs a refresh',
  approval_expired: 'Approval expired',
  agent_off: 'Agent turned off',
  policy_changed: 'Policy changed',
  play_deleted: 'Play deleted',
  write_blocked: 'CRM writes off',
  unverified: "Couldn't verify",
  now_excluded: 'Now excluded',
};

/** A group's status as its chip reads; null for an open group with nothing to say. */
export function statusLabel(
  row: Pick<DuplicateGroupRow, 'status' | 'dismissKind' | 'statusReason'>
): string | null {
  switch (row.status) {
    case 'open':
      return row.statusReason
        ? (STATUS_REASON_WORDS[row.statusReason] ?? null)
        : null;
    case 'approved':
      return 'Queued to merge';
    case 'merging':
    case 'verify_pending':
      return 'Merging';
    case 'merged':
      return 'Merged';
    case 'failed':
      return 'Failed';
    case 'skipped_changed':
      return 'Changed since review';
    case 'dismissed':
      return row.dismissKind === 'not_duplicates'
        ? 'Not duplicates'
        : 'Skipped';
    case 'superseded':
      return 'Replaced by a newer scan';
  }
}

/** Merging or queued: the screen polls while any group is. */
export function isMerging(status: DuplicateGroupRow['status']): boolean {
  return (
    status === 'approved' || status === 'merging' || status === 'verify_pending'
  );
}

/** "2 merged, 1 failed": a merge's per-record results, the kept record left out. */
export function memberResultWords(
  row: Pick<DuplicateGroupRow, 'memberResults' | 'memberCount' | 'status'>
): string {
  const results = row.memberResults;
  if (
    !results ||
    !(
      isMerging(row.status) ||
      row.status === 'merged' ||
      row.status === 'failed'
    )
  )
    return '';
  const merged = results.merged ?? 0;
  const failed =
    (results.failed ?? 0) + (results.unverified ?? 0) + (results.skipped ?? 0);
  const losers = Math.max(0, row.memberCount - 1);
  const parts = [
    merged.toLocaleString() + ' of ' + losers.toLocaleString() + ' merged',
  ];
  if (failed > 0) parts.push(failed.toLocaleString() + ' failed');
  return parts.join(', ');
}

/* ── Who may act ───────────────────────────────────────────────────────── */

export interface ReviewPermissions {
  /** The agent is on: merges run only then. */
  agentOn: boolean;
  /** An org admin or the person who connected the CRM. */
  canDecide: boolean;
  connectorName: string | null;
}

/** Why the viewer can't dismiss, reopen or pick the kept record; null when they can. */
export function decideBlockedReason(
  permissions: Pick<ReviewPermissions, 'canDecide' | 'connectorName'>
): string | null {
  if (permissions.canDecide) return null;
  return permissions.connectorName
    ? 'Admin or ' + permissions.connectorName + ' only'
    : 'Admin or CRM connector only';
}

/** Why the viewer can't merge; null when they can. */
export function mergeBlockedReason(
  permissions: ReviewPermissions
): string | null {
  return (
    decideBlockedReason(permissions) ??
    (permissions.agentOn ? null : 'Turn on to merge')
  );
}

/** An open group the play can merge: the rows a checkbox selects. */
export function isSelectable(
  row: Pick<DuplicateGroupRow, 'status' | 'mode'>
): boolean {
  return row.status === 'open' && row.mode !== 'report_only';
}

export type RowAction =
  | 'merge'
  | 'not_duplicates'
  | 'skip'
  | 'reopen'
  | 'refresh';

/** The actions a row offers in its status. */
export function rowActions(
  row: Pick<DuplicateGroupRow, 'status' | 'mode' | 'statusReason'>,
  hasMerge: boolean
): RowAction[] {
  switch (row.status) {
    case 'open': {
      const actions: RowAction[] = [];
      if (hasMerge && row.mode !== 'report_only') actions.push('merge');
      actions.push('not_duplicates', 'skip');
      if (row.statusReason === 'needs_refresh') actions.push('refresh');
      return actions;
    }
    case 'dismissed':
      return ['reopen'];
    case 'failed':
    case 'skipped_changed':
      return ['refresh'];
    default:
      return [];
  }
}

export const ROW_ACTION_LABEL: Record<RowAction, string> = {
  merge: 'Merge',
  not_duplicates: 'Not duplicates',
  skip: 'Skip',
  reopen: 'Reopen',
  refresh: 'Refresh',
};

/* ── Order and the merge confirm ───────────────────────────────────────── */

/**
 * The screen's order: unflagged before flagged, and within each, a
 * highlighted run's groups first; otherwise the server's order. The list
 * route orders the whole list this way (`run` asks for that scan first), so
 * here it only breaks what one page leaves tied.
 */
export function sortGroups<
  T extends Pick<DuplicateGroupRow, 'flags' | 'scanRunId'>,
>(rows: readonly T[], run?: string): T[] {
  const rank = (row: T) =>
    (row.flags.length ? 2 : 0) + (run && row.scanRunId !== run ? 1 : 0);
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => rank(a.row) - rank(b.row) || a.index - b.index)
    .map(({ row }) => row);
}

/**
 * The loaded pages as the screen lists them: page after page, in the
 * server's order, each page sorted on its own. Sorting across pages would
 * lift a later page's unflagged groups above an earlier page's flagged ones
 * on Show more, moving rows under the reviewer and the J and K keys.
 */
export function groupsInOrder<
  T extends Pick<DuplicateGroupRow, 'flags' | 'scanRunId'>,
>(pages: readonly { groups: readonly T[] }[], run?: string): T[] {
  return pages.flatMap((page) => sortGroups(page.groups, run));
}

/**
 * The merge confirm (design 10.3), in the play's CRM. Merging one group, it
 * names the record kept: the group's name is the kept record's (or its
 * email).
 */
export function mergeConfirm(
  count: number,
  crmType: CleanupCrm | undefined,
  group?: Pick<DuplicateGroupRow, 'displayName' | 'object' | 'memberCount'>
): { title: string; body: string; action: string } {
  const others = group ? Math.max(0, group.memberCount - 1) : 0;
  const kept = group
    ? 'Keeps ' +
      (group.displayName?.trim() || 'one ' + objectWords(group.object, 1)) +
      ' and merges the other ' +
      (others === 1 ? '' : others.toLocaleString() + ' ') +
      objectWords(group.object, others) +
      ' into it. '
    : '';
  return {
    title:
      'Merge ' +
      count.toLocaleString() +
      (count === 1 ? ' group' : ' groups') +
      ' in ' +
      cleanupCrmName(crmType) +
      '?',
    body:
      kept + "Related records move to the kept record. This can't be undone.",
    action:
      count === 1
        ? 'Merge group'
        : 'Merge ' + count.toLocaleString() + ' groups',
  };
}

/** A refused decide: its code, and for a set that grew, how many it covers now. */
export function decideConflict(
  error: unknown
): { code: string | null; count: number | null } | null {
  const failure = error as { status?: unknown; data?: unknown } | undefined;
  if (failure?.status !== 409) return null;
  const data = (failure.data ?? {}) as {
    error?: { details?: Record<string, unknown> };
    detail?: unknown;
  };
  const details =
    data.error?.details ??
    (data.detail && typeof data.detail === 'object'
      ? (data.detail as Record<string, unknown>)
      : {});
  const code = typeof details.code === 'string' ? details.code : null;
  const count =
    typeof details.count === 'number' && Number.isFinite(details.count)
      ? details.count
      : null;
  return { code, count };
}

/** The CSV's file name: "Acme cleanup duplicates 2026-09-25.csv". */
export function exportFileName(agentName: string, now = new Date()): string {
  const name = (agentName.trim() || 'agent').replace(/[\\/:*?"<>|]+/g, '_');
  return name + ' duplicates ' + now.toISOString().slice(0, 10) + '.csv';
}

/* ── The column diff ───────────────────────────────────────────────────── */

export function isBlank(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  );
}

/** A field value as a cell shows it. */
export function fieldText(value: unknown): string {
  if (isBlank(value)) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string') return value.trim();
  return JSON.stringify(value);
}

function createdAt(member: Pick<DuplicateMember, 'createdAt'>): Date | null {
  const at = member.createdAt ? new Date(member.createdAt) : null;
  return at && !Number.isNaN(at.getTime()) ? at : null;
}

/**
 * "Created Sep 25, 2026": the day, with the time when another record in the
 * group was created that day and the seconds when in the same minute, so the
 * record kept as the oldest reads older than the rest. Empty when unknown.
 */
export function createdWords(
  member: Pick<DuplicateMember, 'createdAt'>,
  members: readonly Pick<DuplicateMember, 'createdAt'>[]
): string {
  const at = createdAt(member);
  if (!at) return '';
  const others = members
    .filter((other) => other !== member)
    .map(createdAt)
    .filter((other): other is Date => other !== null);
  const minute = (date: Date) => Math.floor(date.getTime() / 60_000);
  const sameDay = others.some((o) => o.toDateString() === at.toDateString());
  const sameMinute = others.some((o) => minute(o) === minute(at));
  return (
    'Created ' +
    at.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(sameDay ? { hour: 'numeric', minute: '2-digit' } : {}),
      ...(sameMinute ? { second: '2-digit' } : {}),
    })
  );
}

/** A member's name as its column reads. */
export function memberName(
  member: Pick<DuplicateMember, 'displayName' | 'email' | 'crmId'>
): string {
  return member.displayName?.trim() || member.email?.trim() || member.crmId;
}

/** Ids and system fields the diff leaves out. */
// Ids and the CRM's own timestamps: they always differ and never decide a
// merge (each record's created date heads its column).
const HIDDEN_FIELDS = new Set([
  'id',
  'isdeleted',
  'masterrecordid',
  'createddate',
  'createdbyid',
  'lastmodifieddate',
  'lastmodifiedbyid',
  'systemmodstamp',
  'lastreferenceddate',
  'lastvieweddate',
  'cleanstatus',
  'createdate',
  'hs_lastmodifieddate',
  'hs_object_id',
]);

export interface DiffCell {
  text: string;
  /** The kept record's blank that the merge fills, and whose value it takes. */
  filledFrom: string | null;
}

export interface DiffRow {
  field: string;
  /** One per member, in the members' order. */
  cells: DiffCell[];
  /** Members hold different values here (a blank counts). */
  differs: boolean;
  /** Every member leaves the field blank. */
  blank: boolean;
}

/** Fills first, then the fields where members differ, then the rest, blanks last. */
function diffRank(row: DiffRow): number {
  if (row.cells.some((cell) => cell.filledFrom)) return 0;
  if (row.differs) return 1;
  return row.blank ? 3 : 2;
}

/**
 * The fields of a group, members as columns: every field the scan kept, a
 * row each, marked where members differ or all are blank, and on the kept
 * record the blanks the merge fills (and the opt-outs it sets) with the
 * member each value comes from. The fields the merge fills come first, then
 * the ones where members differ, then the rest and the blanks last, each in
 * the scan's order.
 */
export function diffRows(
  detail: Pick<DuplicateGroupDetail, 'members' | 'fieldPlan' | 'keptId'>
): DiffRow[] {
  const members = detail.members;
  const names: string[] = [];
  for (const member of members)
    for (const field of Object.keys(member.fields ?? {}))
      if (!names.includes(field) && !HIDDEN_FIELDS.has(field.toLowerCase()))
        names.push(field);
  const byId = new Map(members.map((member) => [member.crmId, member]));
  const planned = new Map<string, { value: unknown; from: string }>();
  for (const entry of [
    ...(detail.fieldPlan?.fills ?? []),
    ...(detail.fieldPlan?.optOuts ?? []),
  ])
    planned.set(entry.field, { value: entry.value, from: entry.from });
  const rows = names.map((field): DiffRow => {
    const values = members.map((member) => member.fields?.[field]);
    const texts = values.map(fieldText);
    const differs = new Set(texts.map((text) => text.toLowerCase())).size > 1;
    const plan = planned.get(field);
    const cells = members.map((member, index): DiffCell => {
      if (plan && member.crmId === detail.keptId) {
        const source = byId.get(plan.from);
        return {
          text: fieldText(plan.value),
          filledFrom: source ? memberName(source) : plan.from,
        };
      }
      return { text: texts[index], filledFrom: null };
    });
    return {
      field,
      differs,
      blank: cells.every((cell) => !cell.text),
      cells,
    };
  });
  // Array sort is stable, so each rank keeps the scan's order.
  return rows.sort((a, b) => diffRank(a) - diffRank(b));
}

/** "Contact", "contacts": the object in a sentence. */
export function objectWords(object: string, count: number): string {
  return cleanupObjectNoun(object, count !== 1);
}
